import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronDown, Globe } from 'lucide-react';

const VVS_LOGO = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/yqc71nna_FromNow2.jpg';

// Language options with native names
const LANG_OPTIONS = [
  { code: 'EN', name: 'English' },
  { code: 'FR', name: 'Français' },
  { code: 'NL', name: 'Nederlands' },
  { code: 'DE', name: 'Deutsch' },
  { code: 'ES', name: 'Español' },
  { code: 'PT', name: 'Português' },
  { code: 'IT', name: 'Italiano' },
  { code: 'JA', name: '日本語' },
];

// NavLink component with animated underline
function NavLink({ link, isActive, t }) {
  const [hovered, setHovered] = useState(false);
  const lineRef = useRef(null);

  useEffect(() => {
    if (!lineRef.current) return;
    anime.remove(lineRef.current);
    if (hovered) {
      anime({
        targets: lineRef.current,
        scaleX: 1,
        duration: 300,
        easing: 'easeOutExpo',
      });
    } else {
      anime({
        targets: lineRef.current,
        scaleX: 0,
        duration: 220,
        easing: 'easeInQuad',
      });
    }
  }, [hovered]);

  const isFairflow = link.special;

  return (
    <Link
      to={link.path}
      data-testid={`nav-${link.key.split('.')[1]}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '11px',
        fontWeight: 500,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        color: isActive ? 'var(--vvs-cyan)' : isFairflow ? 'transparent' : 'var(--vvs-muted)',
        transition: 'color 0.3s ease',
        padding: isFairflow ? '5px 14px' : '4px 0',
        border: isFairflow ? '1px solid transparent' : 'none',
        borderRadius: isFairflow ? '3px' : '0',
        background: isFairflow 
          ? 'linear-gradient(135deg, rgba(138,92,246,0.8), rgba(59,130,246,0.8))' 
          : 'transparent',
        backgroundClip: isFairflow ? 'padding-box, text' : 'unset',
        WebkitBackgroundClip: isFairflow ? 'padding-box, text' : 'unset',
      }}
    >
      <span style={{
        color: isFairflow ? '#fff' : 'inherit',
      }}>
        {t(link.key)}
      </span>
      
      {/* Animated underline - only for non-fairflow links */}
      {!isFairflow && (
        <span
          ref={lineRef}
          style={{
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '100%',
            height: '1px',
            background: 'var(--vvs-cyan)',
            transformOrigin: 'left',
            transform: 'scaleX(0)',
          }}
        />
      )}
    </Link>
  );
}

// External link with animated underline
function ExternalNavLink({ link, t }) {
  const [hovered, setHovered] = useState(false);
  const lineRef = useRef(null);
  const isFairflow = link.special;

  useEffect(() => {
    if (!lineRef.current) return;
    anime.remove(lineRef.current);
    if (hovered) {
      anime({
        targets: lineRef.current,
        scaleX: 1,
        duration: 300,
        easing: 'easeOutExpo',
      });
    } else {
      anime({
        targets: lineRef.current,
        scaleX: 0,
        duration: 220,
        easing: 'easeInQuad',
      });
    }
  }, [hovered]);

  return (
    <a
      href={link.path}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`nav-${link.key.split('.')[1]}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '11px',
        fontWeight: 500,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        padding: isFairflow ? '5px 14px' : '4px 0',
        border: isFairflow ? '1px solid transparent' : 'none',
        borderRadius: isFairflow ? '3px' : '0',
        background: isFairflow 
          ? 'linear-gradient(135deg, rgba(138,92,246,0.7), rgba(59,130,246,0.7))' 
          : 'transparent',
        transition: 'all 0.3s ease',
        animation: isFairflow ? 'fairflow-shimmer 3s ease-in-out infinite' : 'none',
      }}
    >
      <span style={{ 
        color: isFairflow ? '#fff' : 'var(--vvs-muted)',
        transition: 'color 0.3s',
      }}>
        {t(link.key)}
      </span>
      
      {/* Animated underline */}
      {!isFairflow && (
        <span
          ref={lineRef}
          style={{
            position: 'absolute',
            bottom: '0',
            left: '0',
            width: '100%',
            height: '1px',
            background: 'var(--vvs-cyan)',
            transformOrigin: 'left',
            transform: 'scaleX(0)',
          }}
        />
      )}
    </a>
  );
}

// Music Dropdown component for desktop
function MusicDropdown({ t, location }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const MUSIC_LINKS = [
    { key: 'nav.releases', path: '/releases' },
    { key: 'nav.artists', path: '/artists' },
    { key: 'nav.events', path: '/events' },
  ];

  const isActive = ['/releases', '/artists', '/events'].some(p => location.pathname.startsWith(p));

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ position: 'relative' }}
    >
      <button
        data-testid="nav-music-dropdown"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'Space Grotesk, sans-serif',
          fontSize: '11px',
          fontWeight: 500,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: isActive ? 'var(--vvs-cyan)' : 'var(--vvs-muted)',
          transition: 'color 0.3s ease',
          padding: '4px 0',
        }}
      >
        {t('nav.music')}
        <ChevronDown 
          size={14} 
          style={{ 
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }} 
        />
      </button>

      {/* Dropdown menu */}
      <div 
        style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '12px',
          background: 'rgba(5, 10, 20, 0.98)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(240,244,248,0.08)',
          borderRadius: '4px',
          padding: '8px 0',
          minWidth: '160px',
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.25s ease, visibility 0.25s ease',
          zIndex: 1000,
        }}
      >
        {MUSIC_LINKS.map(link => (
          <DropdownItem 
            key={link.path} 
            link={link} 
            t={t} 
            isActive={location.pathname.startsWith(link.path)}
          />
        ))}
      </div>
    </div>
  );
}

// Dropdown item with animated underline
function DropdownItem({ link, t, isActive }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={link.path}
      data-testid={`nav-${link.key.split('.')[1]}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        padding: '10px 20px',
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '11px',
        fontWeight: 500,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        textDecoration: 'none',
        color: isActive ? 'var(--vvs-cyan)' : hovered ? '#F0F4F8' : 'var(--vvs-muted)',
        background: hovered ? 'rgba(240,244,248,0.03)' : 'transparent',
        transition: 'all 0.2s ease',
        borderLeft: isActive ? '2px solid var(--vvs-cyan)' : '2px solid transparent',
      }}
    >
      {t(link.key)}
    </Link>
  );
}

// Language selector with globe icon and dropdown
function LanguageSelector({ lang, setLang }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    setLang(code);
    setIsOpen(false);
  };

  return (
    <div 
      ref={dropdownRef}
      style={{ 
        position: 'relative',
        borderLeft: '1px solid rgba(240,244,248,0.1)', 
        paddingLeft: '16px', 
        marginLeft: '4px',
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-selector"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--vvs-muted)',
          transition: 'color 0.3s',
          padding: '4px',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#F0F4F8'}
        onMouseLeave={e => !isOpen && (e.currentTarget.style.color = 'var(--vvs-muted)')}
      >
        <Globe size={14} />
        <span style={{
          fontFamily: 'Space Grotesk, sans-serif',
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '0.1em',
          color: 'var(--vvs-cyan)',
        }}>
          {lang}
        </span>
      </button>

      {/* Dropdown */}
      <div style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: '12px',
        background: 'rgba(10, 22, 40, 0.98)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(0, 180, 255, 0.15)',
        borderRadius: '6px',
        padding: '8px 0',
        minWidth: '140px',
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? 'visible' : 'hidden',
        transform: isOpen ? 'translateY(0)' : 'translateY(-8px)',
        transition: 'opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease',
        zIndex: 1000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        {LANG_OPTIONS.map(l => (
          <button
            key={l.code}
            onClick={() => handleSelect(l.code)}
            data-testid={`lang-${l.code.toLowerCase()}`}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '10px 16px',
              background: lang === l.code ? 'rgba(0, 180, 255, 0.08)' : 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontFamily: l.code === 'JA' ? 'sans-serif' : 'Space Grotesk, sans-serif',
              fontSize: '12px',
              color: lang === l.code ? 'var(--vvs-cyan)' : '#F0F4F8',
              transition: 'background 0.2s, color 0.2s',
            }}
            onMouseEnter={e => {
              if (lang !== l.code) {
                e.currentTarget.style.background = 'rgba(240,244,248,0.05)';
              }
            }}
            onMouseLeave={e => {
              if (lang !== l.code) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {l.name}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [musicExpanded, setMusicExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoHovered, setLogoHovered] = useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useLanguage();
  const logoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Desktop nav links (excluding music sub-items)
  const NAV_LINKS = [
    { key: 'nav.vision', path: '/vision' },
    { key: 'nav.partners', path: '/partners' },
    { key: 'nav.contact', path: '/contact' },
    { key: 'nav.fairflow', path: 'https://fairflow.be', special: true, external: true },
  ];

  // Music sub-links for mobile
  const MUSIC_LINKS = [
    { key: 'nav.releases', path: '/releases' },
    { key: 'nav.artists', path: '/artists' },
    { key: 'nav.events', path: '/events' },
  ];

  // Check for mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setMusicExpanded(false); }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Logo entrance animation
  useEffect(() => {
    if (logoRef.current) {
      anime({
        targets: logoRef.current,
        translateY: [-8, 0],
        opacity: [0, 1],
        duration: 800,
        easing: 'easeOutExpo',
        delay: 200,
      });
    }
  }, []);

  useEffect(() => {
    if (menuOpen) {
      setTimeout(() => {
        anime({
          targets: '.mobile-nav-link',
          translateY: [-16, 0],
          opacity: [0, 1],
          duration: 350,
          delay: anime.stagger(55),
          easing: 'easeOutExpo',
        });
      }, 50);
    }
  }, [menuOpen]);

  return (
    <>
      {/* Fairflow shimmer animation */}
      <style>
        {`
          @keyframes fairflow-shimmer {
            0%, 100% {
              box-shadow: 0 0 0 rgba(138,92,246,0);
            }
            50% {
              box-shadow: 0 0 12px rgba(138,92,246,0.3), 0 0 24px rgba(59,130,246,0.2);
            }
          }
          
          @keyframes logo-glow {
            0%, 100% {
              filter: drop-shadow(0 0 0 rgba(100,180,255,0));
            }
            50% {
              filter: drop-shadow(0 0 8px rgba(100,180,255,0.4));
            }
          }
        `}
      </style>
      
      <nav
        data-testid="navbar"
        style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          zIndex: 100, 
          padding: isMobile ? '0 20px' : '0 32px', 
          height: '64px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: scrolled ? 'rgba(5, 10, 20, 0.94)' : 'rgba(5, 10, 20, 0.55)',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          borderBottom: scrolled ? '1px solid rgba(240,244,248,0.07)' : '1px solid transparent',
          transition: 'background 0.5s ease, border-color 0.5s ease',
        }}
      >
        {/* Logo with glow effect */}
        <Link 
          to="/" 
          ref={logoRef}
          data-testid="nav-logo" 
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            textDecoration: 'none',
            opacity: 0,
            filter: logoHovered ? 'drop-shadow(0 0 12px rgba(100,180,255,0.6))' : 'none',
            transition: 'filter 0.4s ease',
          }}
        >
          <img 
            src={VVS_LOGO} 
            alt="VVS" 
            style={{ 
              width: '38px', 
              height: '38px', 
              objectFit: 'cover', 
              borderRadius: '50%', 
              opacity: 0.95,
              transition: 'transform 0.3s ease',
              transform: logoHovered ? 'scale(1.08)' : 'scale(1)',
            }} 
          />
        </Link>

        {/* Desktop nav with MUSIC dropdown */}
        <div style={{ 
          gap: '42px', 
          alignItems: 'center',
          display: isMobile ? 'none' : 'flex',
        }}>
          {/* Vision link */}
          <NavLink
            link={{ key: 'nav.vision', path: '/vision' }}
            isActive={location.pathname.startsWith('/vision')}
            t={t}
          />

          {/* MUSIC dropdown */}
          <MusicDropdown t={t} location={location} />

          {/* Partners, Contact, About */}
          {NAV_LINKS.filter(l => l.path !== '/vision').map(link => (
            link.external ? (
              <ExternalNavLink key={link.path} link={link} t={t} />
            ) : (
              <NavLink
                key={link.path}
                link={link}
                isActive={location.pathname.startsWith(link.path) && link.path !== '/'}
                t={t}
              />
            )
          ))}

          {/* Language selector with globe */}
          <LanguageSelector lang={lang} setLang={setLang} />
        </div>

        {/* Hamburger - visible only on mobile */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="nav-hamburger"
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            padding: '12px', 
            color: '#F0F4F8',
            display: isMobile ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '44px',
            minHeight: '44px',
          }}
          aria-label="Toggle menu"
        >
          <div style={{ 
            width: '22px', 
            height: '2px', 
            background: menuOpen ? 'var(--vvs-cyan)' : '#F0F4F8', 
            marginBottom: '5px', 
            transition: 'all 0.3s',
            transform: menuOpen ? 'rotate(45deg) translateY(7px)' : 'none',
          }} />
          <div style={{ 
            width: '14px', 
            height: '2px', 
            background: menuOpen ? 'var(--vvs-cyan)' : '#F0F4F8', 
            marginBottom: '5px', 
            transition: 'all 0.3s',
            opacity: menuOpen ? 0 : 1,
          }} />
          <div style={{ 
            width: '22px', 
            height: '2px', 
            background: menuOpen ? 'var(--vvs-cyan)' : '#F0F4F8', 
            transition: 'all 0.3s',
            transform: menuOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
          }} />
        </button>

        {/* Mobile menu - full screen overlay */}
        {menuOpen && (
          <div 
            data-testid="mobile-menu" 
            style={{
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100vw',
              height: '100vh',
              background: '#050a14',
              zIndex: 99999,
              display: 'flex',
              flexDirection: 'column',
              isolation: 'isolate',
            }}
          >
            {/* Solid background overlay to ensure opacity */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, #050a14 0%, #060c18 100%)',
              zIndex: -1,
            }} />
            
            {/* Menu header with logo and close */}
            <div style={{
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 20px',
              borderBottom: '1px solid rgba(240,244,248,0.08)',
              flexShrink: 0,
            }}>
              <img src={VVS_LOGO} alt="VVS" style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '50%' }} />
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--vvs-cyan)',
                  padding: '12px',
                  cursor: 'pointer',
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Menu content - scrollable */}
            <div style={{ 
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              padding: '20px 24px 100px',
            }}>
              {/* VISION link */}
              <Link 
                to="/vision"
                className="mobile-nav-link" 
                onClick={() => setMenuOpen(false)}
                style={{
                  fontFamily: 'Syne, sans-serif', 
                  fontSize: '20px', 
                  fontWeight: 700,
                  letterSpacing: '0.04em', 
                  textTransform: 'uppercase', 
                  textDecoration: 'none',
                  color: location.pathname === '/vision' ? 'var(--vvs-cyan)' : '#F0F4F8', 
                  padding: '16px 0',
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(240,244,248,0.08)',
                  opacity: 0,
                }}
              >
                {t('nav.vision')}
              </Link>

              {/* MUSIC section with sub-items */}
              <div style={{ borderBottom: '1px solid rgba(240,244,248,0.08)' }}>
                <button
                  onClick={() => setMusicExpanded(!musicExpanded)}
                  className="mobile-nav-link"
                  style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Syne, sans-serif', 
                    fontSize: '20px', 
                    fontWeight: 700,
                    letterSpacing: '0.04em', 
                    textTransform: 'uppercase', 
                    textDecoration: 'none',
                    textAlign: 'left',
                    color: ['/releases', '/artists', '/events'].some(p => location.pathname.startsWith(p)) ? 'var(--vvs-cyan)' : '#F0F4F8',
                    padding: '16px 0',
                    minHeight: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    opacity: 0,
                  }}
                >
                  {t('nav.music')}
                  <ChevronDown 
                    size={20} 
                    style={{ 
                      transition: 'transform 0.3s ease',
                      transform: musicExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      color: 'var(--vvs-muted)',
                    }} 
                  />
                </button>
                
                {/* Music sub-links - indented */}
                <div style={{
                  maxHeight: musicExpanded ? '200px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                }}>
                  {MUSIC_LINKS.map(link => (
                    <Link 
                      key={link.path}
                      to={link.path}
                      onClick={() => setMenuOpen(false)}
                      style={{
                        display: 'block',
                        fontFamily: 'Space Grotesk, sans-serif', 
                        fontSize: '14px', 
                        fontWeight: 500,
                        letterSpacing: '0.1em', 
                        textTransform: 'uppercase', 
                        textDecoration: 'none',
                        color: location.pathname === link.path ? 'var(--vvs-cyan)' : 'var(--vvs-muted)', 
                        padding: '12px 0 12px 24px',
                        borderLeft: location.pathname === link.path ? '2px solid var(--vvs-cyan)' : '2px solid transparent',
                        marginLeft: '8px',
                      }}
                    >
                      {t(link.key)}
                    </Link>
                  ))}
                </div>
              </div>

              {/* PARTNERS link */}
              <Link 
                to="/partners"
                className="mobile-nav-link" 
                onClick={() => setMenuOpen(false)}
                style={{
                  fontFamily: 'Syne, sans-serif', 
                  fontSize: '20px', 
                  fontWeight: 700,
                  letterSpacing: '0.04em', 
                  textTransform: 'uppercase', 
                  textDecoration: 'none',
                  color: location.pathname === '/partners' ? 'var(--vvs-cyan)' : '#F0F4F8', 
                  padding: '16px 0',
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(240,244,248,0.08)',
                  opacity: 0,
                }}
              >
                {t('nav.partners')}
              </Link>

              {/* CONTACT link */}
              <Link 
                to="/contact"
                className="mobile-nav-link" 
                onClick={() => setMenuOpen(false)}
                style={{
                  fontFamily: 'Syne, sans-serif', 
                  fontSize: '20px', 
                  fontWeight: 700,
                  letterSpacing: '0.04em', 
                  textTransform: 'uppercase', 
                  textDecoration: 'none',
                  color: location.pathname === '/contact' ? 'var(--vvs-cyan)' : '#F0F4F8', 
                  padding: '16px 0',
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(240,244,248,0.08)',
                  opacity: 0,
                }}
              >
                {t('nav.contact')}
              </Link>
              
              {/* Fairflow external link */}
              <a 
                href="https://fairflow.be"
                target="_blank" 
                rel="noopener noreferrer" 
                className="mobile-nav-link" 
                onClick={() => setMenuOpen(false)}
                style={{
                  fontFamily: 'Syne, sans-serif', 
                  fontSize: '16px', 
                  fontWeight: 700,
                  letterSpacing: '0.05em', 
                  textTransform: 'uppercase', 
                  textDecoration: 'none',
                  color: '#fff', 
                  background: 'linear-gradient(135deg, rgba(138,92,246,0.7), rgba(59,130,246,0.7))',
                  padding: '14px 20px',
                  borderRadius: '6px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'fit-content',
                  marginTop: '24px',
                  minHeight: '52px',
                  opacity: 0,
                }}
              >
                {t('nav.fairflow')}
              </a>
              
              {/* Language selector */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                paddingTop: '24px', 
                marginTop: '32px',
                borderTop: '1px solid rgba(240,244,248,0.1)',
                flexWrap: 'wrap',
                animation: 'fadeSlideIn 0.3s ease-out 0.4s forwards',
                opacity: 0,
              }}>
                {LANG_OPTIONS.map(l => (
                  <button 
                    key={l.code} 
                    onClick={() => { setLang(l.code); setMenuOpen(false); }} 
                    data-testid={`mobile-lang-${l.code.toLowerCase()}`}
                    style={{
                      background: 'none', 
                      border: lang === l.code ? '1px solid var(--vvs-cyan)' : '1px solid rgba(240,244,248,0.15)', 
                      cursor: 'pointer',
                      fontFamily: l.code === 'JA' ? 'sans-serif' : 'Space Grotesk, sans-serif', 
                      fontSize: '12px', 
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      color: lang === l.code ? 'var(--vvs-cyan)' : 'rgba(148,163,184,0.7)',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      minHeight: '40px',
                    }}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* CSS Animation */}
            <style>
              {`
                @keyframes fadeSlideIn {
                  from {
                    opacity: 0;
                    transform: translateY(-10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}
            </style>
          </div>
        )}
      </nav>
    </>
  );
}
